import { signal, useSignalEffect } from "@preact/signals";
import { createRef, render } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { challengeFor } from "../progression.ts";
import type { Tool } from "../clay.ts";
import { Icon, type IconName } from "./icon.tsx";
import styles from "./kneading.module.css";
import {
  brushNudged,
  hasTutorial,
  startTutorial,
  TutorialCoach,
  tutorialFinishing,
  tutorialTitle,
  tutorialTool,
  undoNudged,
} from "./tutorial.tsx";

/** What the kneading screen shows: the game writes it, the screen follows. */
export const kneading = {
  mood: signal("こねこね…"),
  strokes: signal(0),
  /** The tool strokes use now: the one selected, or the one a held key swaps in. */
  tool: signal<Tool>("grab"),
  symmetric: signal(false),
  wheel: signal(false),
  /** Brush radius as a fraction of the shorter side of the screen. */
  brush: signal(0.1),
  /** Brush radius the last counted stroke worked with, in the target's unscaled units. */
  strokeBrush: signal(0),
  /** Metadata for the last completed stroke, published before its count. */
  strokeTool: signal<Tool>("grab"),
  strokeOnHole: signal(false),
  canUndo: signal(false),
  /** Symmetry mode's name for the target's mirror plane. */
  mirror: signal("左右対称"),
  helpOpen: signal(false),
  /** The request being worked on, as its head over the target says it. */
  request: signal({ id: "", title: "", wish: "" }),
  /** Kneading freely: no request, so no target. */
  free: signal(false),
};

/**
 * The box the target preview is drawn in: the game draws it there on the canvas, under the
 * box (see main.ts, render).
 */
export const targetView = createRef<HTMLDivElement>();

/** What the kneading screen asks of the game. */
export interface KneadingActions {
  selectTool(tool: Tool): void;
  setSymmetric(on: boolean): void;
  setWheel(on: boolean): void;
  /** While the slider moves, and `done` once it is let go. */
  setBrush(size: number, done: boolean): void;
  undo(): void;
  restart(): void;
  submit(): void;
  /** Opens the letters, to take on another request. */
  chooseRequest(): void;
  /** Back to the title screen, the clay left as it is to come back to. */
  toTitle(): void;
}

const TOOLS: { tool: Tool; key: string; label: string; tip: string; help: string }[] = [
  { tool: "grab", key: "1", label: "つまむ", tip: "掴んで引っ張る", help: "掴んで引っ張る" },
  {
    tool: "push",
    key: "2",
    label: "押す",
    tip: "へこませる（Shift＋ドラッグでも）",
    help: "へこませる。周りが盛り上がる（Shift＋ドラッグでも）",
  },
  {
    tool: "inflate",
    key: "3",
    label: "盛る",
    tip: "盛り上げる（Ctrl＋ドラッグでも）",
    help: "盛り上げる。ほかが少し痩せる（Ctrl＋ドラッグでも）",
  },
  {
    tool: "smooth",
    key: "4",
    label: "ならす",
    tip: "なめらかにする",
    help: "なめらかにする。くびれや折れ目も埋まる",
  },
  {
    tool: "flatten",
    key: "5",
    label: "平ら",
    tip: "正面を向いた平らな面にする",
    help: "画面に正面を向いた平らな面にする。向きを変えてこすれば角も作れる",
  },
];

const HELP_ROWS: { icon: IconName; name: string; key: string; text: string }[] = [
  ...TOOLS.map((t) => ({ icon: t.tool, name: t.label, key: t.key, text: t.help })),
  { icon: "mirror", name: "対称", key: "M", text: "反対側も同時にこねる。1 回で 2 手" },
  {
    icon: "wheel",
    name: "ろくろ",
    key: "R",
    text: "粘土を回して、ぐるっと一周こねる。1 回で 4 手",
  },
  { icon: "undo", name: "戻す", key: "Ctrl＋Z", text: "1 手前に戻す。「最初から」も戻せる" },
];

const HELP_SEEN = "kneading-needed.help-seen";

/** What the lesson points at until it is used: framed, and the frame bobbing. */
const NUDGE = `${styles.tutorialFocus} ${styles.tutorialNudge}`;

/** What to do once the help is closed (back to the title screen, when opened from there). */
let afterHelp: (() => void) | undefined;

/**
 * Opens the help, `onClose` running once it is closed; or closes it. Closing it once marks it
 * seen.
 */
export function showHelp(open: boolean, onClose?: () => void): void {
  kneading.helpOpen.value = open;
  if (open) {
    afterHelp = onClose;
    return;
  }
  markHelpSeen();
  const after = afterHelp;
  afterHelp = undefined;
  after?.();
}

/** Remembers that another first-work guide has already introduced the controls. */
export function markHelpSeen(): void {
  try {
    localStorage.setItem(HELP_SEEN, "1");
  } catch {
    // No storage: it shows again next time.
  }
}

/** Opens the help by itself the first time the clay is kneaded (in this browser). */
export function showHelpOnce(): void {
  let seen = false;
  try {
    seen = localStorage.getItem(HELP_SEEN) !== null;
  } catch {
    // No storage: shown every time.
  }
  if (!seen) showHelp(true);
}

/**
 * The kneading screen: how it is going (top left), the target (top right), the tools (bottom
 * centre), undoing and help (bottom left), done (bottom right), and the help card over it all.
 */
export function mountKneading(root: HTMLElement, actions: KneadingActions): void {
  render(<KneadingScreen actions={actions} />, root);
}

function KneadingScreen({ actions }: { actions: KneadingActions }) {
  return (
    <>
      <div class={styles.root}>
        <Status />
        <Target actions={actions} />
        <Dock actions={actions} />
        <div class={styles.corner}>
          <button
            type="button"
            class={styles.round}
            aria-label="タイトルに戻る"
            data-tip="タイトルに戻る"
            onClick={() => actions.toTitle()}
          >
            <Icon name="home" />
          </button>
          <button
            type="button"
            class={`${styles.round} ${styles.undo} ${undoNudged() ? NUDGE : ""}`}
            aria-label="戻す"
            data-tip="戻す（Ctrl＋Z）"
            disabled={!kneading.canUndo.value}
            onClick={() => actions.undo()}
          >
            <Icon name="undo" />
          </button>
          <button
            type="button"
            class={styles.round}
            aria-label="最初から"
            data-tip="最初から"
            onClick={() => actions.restart()}
          >
            <Icon name="restart" />
          </button>
          <button
            type="button"
            class={styles.round}
            aria-label="遊び方"
            data-tip="遊び方"
            onClick={() => showHelp(true)}
          >
            ?
          </button>
        </div>
        <button
          type="button"
          class={`${styles.done} ${tutorialFinishing() ? styles.tutorialFocus : ""}`}
          onClick={() => actions.submit()}
        >
          <Icon name="paw" class={styles.doneIcon} />できた！
        </button>
        <TutorialCoach
          strokes={kneading.strokes}
          strokeBrush={kneading.strokeBrush}
          brush={kneading.brush}
          strokeTool={kneading.strokeTool}
          strokeOnHole={kneading.strokeOnHole}
          selectTool={actions.selectTool}
        />
      </div>
      <Help />
    </>
  );
}

/** The kitten says how close it looks, with the strokes counted beside. */
function Status() {
  const mood = useRef<HTMLParagraphElement>(null);
  const paw = useRef<HTMLSpanElement>(null), counted = useRef(kneading.strokes.peek());
  const text = kneading.mood.value;
  useEffect(() => {
    // A new word from the kitten pops up.
    mood.current?.animate(
      [{ transform: "scale(0.9)" }, { transform: "scale(1.06)" }, { transform: "scale(1)" }],
      { duration: 300, easing: "ease-out" },
    );
  }, [text]);
  useSignalEffect(() => {
    // Another stroke counted: the paw print is pressed on again.
    const n = kneading.strokes.value;
    if (n > counted.current) {
      paw.current?.animate(
        [
          { transform: "rotate(-14deg) scale(1.6)", opacity: 0.3 },
          { transform: "rotate(-14deg) scale(0.85)", opacity: 1 },
          { transform: "rotate(-14deg) scale(1)" },
        ],
        { duration: 360, easing: "ease-out" },
      );
    }
    counted.current = n;
  });
  return (
    <header class={`panel ${styles.status}`}>
      <KittenFace />
      <p class={styles.mood} ref={mood} aria-live="polite">{text}</p>
      <p class={styles.strokes}>
        <b>{kneading.strokes}</b>手
        <span class={styles.paw} ref={paw}>
          <Icon name="paw" />
        </span>
      </p>
    </header>
  );
}

/** What to make: the request's head (a way back to the letters), over the target preview. */
function Target({ actions }: { actions: KneadingActions }) {
  if (kneading.free.value) return null;
  const { title, wish, id } = kneading.request.value;
  const challenge = challengeFor(id);
  return (
    <aside class={styles.target}>
      <button
        type="button"
        class={`panel ${styles.targetHead}`}
        aria-label="依頼を選ぶ"
        onClick={() => actions.chooseRequest()}
      >
        <span class={styles.requestTitle}>{title}</span>
        <span class={styles.requestWish}>こだわり: {wish}</span>
        {challenge && (
          <span class={styles.requestWish}>
            お題: B以上（70点）で納品 · 手数は自由
          </span>
        )}
      </button>
      <div class={styles.targetView} ref={targetView} />
    </aside>
  );
}

function Dock({ actions }: { actions: KneadingActions }) {
  const tool = kneading.tool.value;
  const guidedTool = tutorialTool();
  const brushWay = brushNudged();
  // Chevrons running the way the slider is to go, by the end it is to go to.
  const chevrons = (
    <span
      class={`${styles.brushWay} ${brushWay === "smaller" ? styles.leftward : ""}`}
      aria-hidden="true"
    >
      <i />
      <i />
      <i />
    </span>
  );
  return (
    <div class={`panel ${styles.dock}`}>
      <label class={`${styles.brush} ${brushWay ? NUDGE : ""}`}>
        <span class={`${styles.brushDot} ${styles.small}`} aria-hidden="true" />
        {brushWay === "smaller" && chevrons}
        <input
          type="range"
          min="0.03"
          max="0.2"
          step="0.005"
          value={kneading.brush.value}
          aria-label="ブラシの大きさ"
          onInput={(e) => actions.setBrush(Number(e.currentTarget.value), false)}
          onChange={(e) => actions.setBrush(Number(e.currentTarget.value), true)}
        />
        {brushWay === "bigger" && chevrons}
        <span class={`${styles.brushDot} ${styles.big}`} aria-hidden="true" />
      </label>
      <div class={styles.row}>
        <div class={styles.tools} role="radiogroup" aria-label="ツール">
          {TOOLS.map((t) => (
            <button
              key={t.tool}
              type="button"
              class={`${styles.tool} ${guidedTool === t.tool ? styles.tutorialFocus : ""}`}
              role="radio"
              aria-checked={t.tool === tool}
              data-tip={t.tip}
              onClick={() => actions.selectTool(t.tool)}
            >
              <kbd class={styles.key}>{t.key}</kbd>
              <Icon name={t.tool} />
              {t.label}
            </button>
          ))}
        </div>
        <span class={styles.divider} aria-hidden="true" />
        <Mode
          icon="mirror"
          label={kneading.mirror.value}
          shortcut="M"
          cost={2}
          tip="反対側も同時にこねる。1回で2手"
          on={kneading.symmetric.value}
          toggle={actions.setSymmetric}
        />
        <Mode
          icon="wheel"
          label="ろくろ"
          shortcut="R"
          cost={4}
          tip="粘土を回して一周こねる。1回で4手"
          on={kneading.wheel.value}
          toggle={actions.setWheel}
        />
      </div>
    </div>
  );
}

/** A way of repeating strokes, on or off, with what a stroke then counts for. */
function Mode(
  { icon, label, shortcut, cost, tip, on, toggle }: {
    icon: IconName;
    label: string;
    shortcut: string;
    cost: number;
    tip: string;
    on: boolean;
    toggle: (on: boolean) => void;
  },
) {
  return (
    <button
      type="button"
      class={`${styles.tool} ${styles.mode}`}
      aria-pressed={on}
      data-tip={tip}
      onClick={() => toggle(!on)}
    >
      <kbd class={styles.key}>{shortcut}</kbd>
      <Icon name={icon} />
      {label}
      <span class={styles.cost}>×{cost}</span>
    </button>
  );
}

/** How to knead: a card over the screen, closed with its button, a click beside it or Esc. */
function Help() {
  const open = kneading.helpOpen.value;
  const requestId = kneading.request.value.id;
  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") showHelp(false);
    };
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, [open]);
  if (!open) return null;
  return (
    <div
      class={styles.help}
      onClick={(e) => {
        if (e.target === e.currentTarget) showHelp(false);
      }}
    >
      <section class={`panel ${styles.card}`} role="dialog" aria-labelledby="help-title">
        <h2 class={styles.title} id="help-title">遊び方</h2>
        <p class={styles.lead}>
          依頼の手紙を引き受けたら、右上のおてほんに近づくように粘土をこねて、「できた！」で採点。納品すると、棚に飾れます。粘土はドラッグでこね、道具は画面下のならびから選びます。
        </p>
        <ul class={styles.helpTools}>
          {HELP_ROWS.map((r) => (
            <li key={r.name}>
              <Icon name={r.icon} />
              <b>{r.name}</b>
              <kbd class={styles.helpKey}>{r.key}</kbd>
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
        <ul class={styles.helpNotes}>
          <li>
            背景をドラッグ・右ドラッグで視点を回し、ホイール・ピンチで寄る。ブラシは画面上の大きさが一定なので、寄ると細かく触れる
          </li>
          <li>
            穴の中（向こうが見える所）からこねると、道具が穴に効く。つまむと穴が動き、押すと広がり、盛ると狭まり、ならすとなめらかに、平らで筒になる（ろくろでは効かない）
          </li>
          <li>粘土の体積は一定。押すと周りが盛り上がり、盛ったり伸ばしたりすると他が痩せる</li>
          <li>できたら「できた！」で採点。目標と重ねて見比べられる</li>
        </ul>
        {!kneading.free.value && hasTutorial(requestId) && (
          <button
            type="button"
            class={styles.tutorialReplay}
            onClick={() => {
              showHelp(false);
              startTutorial(requestId);
            }}
          >
            {tutorialTitle(requestId)}をもう一度
          </button>
        )}
        <button type="button" class={`submit ${styles.close}`} onClick={() => showHelp(false)}>
          わかった
        </button>
      </section>
    </div>
  );
}

/** The title screen's kitten, its face only (sized as beside its word, or by `class`). */
export function KittenFace({ class: className = styles.kitten }: { class?: string }) {
  return (
    <svg class={className} viewBox="34 18 132 132" aria-hidden="true">
      <path d="M56 72 L50 24 L92 50 Z" fill="#f2b48c" />
      <path d="M61 63 L57 37 L80 52 Z" fill="#f7c4bb" />
      <path d="M144 72 L150 24 L108 50 Z" fill="#f2b48c" />
      <path d="M139 63 L143 37 L120 52 Z" fill="#f7c4bb" />
      <circle cx="100" cy="94" r="52" fill="#f2b48c" />
      <path
        d="M91 46 v10 M100 44 v12 M109 46 v10"
        stroke="#e39a6a"
        stroke-width="4"
        stroke-linecap="round"
      />
      <ellipse cx="80" cy="92" rx="5.5" ry="7" fill="#4a3530" />
      <ellipse cx="120" cy="92" rx="5.5" ry="7" fill="#4a3530" />
      <circle cx="82" cy="89" r="1.8" fill="#fff" />
      <circle cx="122" cy="89" r="1.8" fill="#fff" />
      <ellipse cx="66" cy="108" rx="9" ry="5" fill="#f28b82" opacity=".45" />
      <ellipse cx="134" cy="108" rx="9" ry="5" fill="#f28b82" opacity=".45" />
      <path
        d="M94 106 q3 4 6 0 q3 4 6 0"
        stroke="#4a3530"
        stroke-width="2.5"
        fill="none"
        stroke-linecap="round"
      />
      <g stroke="#b98062" stroke-width="2" stroke-linecap="round">
        <path d="M58 102 h-20 M58 108 l-18 4" />
        <path d="M142 102 h20 M142 108 l18 4" />
      </g>
    </svg>
  );
}
